import React, { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Select from '../common/Select';
import FormField from '../common/FormField';
import SearchInput from '../common/SearchInput';
import { useToast } from '../../contexts/ToastContext';
import { adminService } from '../../services/adminService';
import { formatRole, formatBusinessType } from '../../utils/enums';

// Vincula um usuário a uma loja com um role per-store específico. Usa busca
// debounced contra /api/admin/stores; backend já paginar+filtra. Pra evitar
// loops curtos, debounce minimalista via setTimeout no efeito.
const AddStoreLinkModal = ({ isOpen, onClose, userId, existingStoreIds = [], onSaved }) => {
  const { error, success } = useToast();
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [roleInStore, setRoleInStore] = useState('reseller');

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(async () => {
      try {
        const data = await adminService.listStores({ q: search, limit: 50, includeInactive: true });
        const rows = data?.rows || data?.data || [];
        // Esconde lojas que o user já tem vínculo — backend bloqueia mesmo,
        // mas sumir antes economiza uma ida ao servidor.
        setStores(rows.filter((s) => !existingStoreIds.includes(s.id)));
      } catch (e) {
        // Silencioso na busca; toast só no submit.
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, isOpen, existingStoreIds]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setStoreId('');
      setRoleInStore('reseller');
    }
  }, [isOpen]);

  const submit = async () => {
    if (!storeId) {
      error('Selecione uma loja');
      return;
    }
    setBusy(true);
    try {
      await adminService.linkUserToStore(userId, {
        storeId: Number(storeId),
        roleInStore
      });
      success('Vínculo criado');
      onSaved?.();
      onClose();
    } catch (e) {
      error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const storeOptions = [
    { value: '', label: 'Selecione…' },
    ...stores.map((s) => ({
      value: String(s.id),
      label: `${s.name} (${formatBusinessType(s.businessType) || '—'})`
    }))
  ];

  const roleOptions = [
    { value: 'client', label: formatRole('client') },
    { value: 'reseller', label: formatRole('reseller') },
    { value: 'staff', label: formatRole('staff') },
    { value: 'owner', label: formatRole('owner') }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Vincular usuário a loja"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} loading={busy}>Vincular</Button>
        </>
      }
      size="md"
    >
      <div className="space-y-3">
        <FormField label="Buscar loja" helpText="Filtra a lista pelo nome.">
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome da loja..." />
        </FormField>
        <FormField label="Loja" required>
          <Select value={storeId} onChange={(e) => setStoreId(e.target.value)} options={storeOptions} />
        </FormField>
        <FormField label="Papel na loja" required helpText="Define o que o usuário pode fazer dentro desta loja.">
          <Select value={roleInStore} onChange={(e) => setRoleInStore(e.target.value)} options={roleOptions} />
        </FormField>
      </div>
    </Modal>
  );
};

export default AddStoreLinkModal;
